import React, { useState, useMemo, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload, FileText, LayoutGrid, Sparkles, Copy, Download, Printer, RefreshCw,
  ExternalLink, CheckCircle2, AlertTriangle, ShieldCheck, FileUp, Search, Loader2, Wand2,
  Plus, X, FileType2, FileDown, MessageSquare, Gauge, Briefcase, Zap, ShieldAlert,
  Building2,
} from "lucide-react";
import WorkforceOsLayout from "../../../components/workforce_os/WorkforceOsLayout";
import RebecaMiguelChatbot from "../../../components/workforce_os/RebecaMiguelChatbot";
import { API } from "../../../lib/api";
import { SUBMIT_RESUME_URL, RECRUITER_SMS_TEL } from "../../../data/recruitingLinks";

/* ===================================================================== */
/*  TRITAL Resume Library™ — Templates                                   */
/*  Original TRITAL-owned section schemas. Public examples used only as  */
/*  general inspiration for ATS-safe section order.                      */
/* ===================================================================== */

const TEMPLATES = [
  { slug: "rn",        name: "RN Travel Resume",         bestFor: "Travel RNs across med-surg, ICU, ER, L&D, peds, oncology, telemetry.",
    sections: ["Unit specialties", "Patient ratios", "Travel/Float", "EMR", "Charge/Preceptor"],
    skills: ["IV starts", "Vasoactive drips", "Charge nurse", "Preceptor", "EKG interpretation", "Telemetry monitoring", "Interdisciplinary rounds", "Travel-ready"] },
  { slug: "rn_staff",  name: "RN Staff / Local Resume",  bestFor: "Permanent + local RN roles — staff nurse, charge, preceptor.",
    sections: ["Unit", "Patient ratios", "EMR", "Charge/Preceptor", "Quality projects"],
    skills: ["Patient education", "Care planning", "Charge nurse", "Preceptor", "Interdisciplinary rounds", "EMR documentation", "Quality improvement"] },
  { slug: "icu_er_medsurg", name: "ICU / ER / Med-Surg Specialty", bestFor: "Critical care, emergency, med-surg, telemetry, stepdown.",
    sections: ["Specialty unit", "Acuity", "Ratios", "Procedures", "Trauma exposure"],
    skills: ["CRRT", "Vasoactive drips", "Vent management", "Trauma response", "Rapid response", "Code blue", "EKG interpretation", "Charge nurse"] },
  { slug: "lpn",       name: "LPN / LVN Resume",         bestFor: "LPN/LVN — LTC, SNF, rehab, primary care.",
    sections: ["LTC/SNF/Rehab", "Med pass", "Wound care", "Injections", "Patient education"],
    skills: ["Med pass", "Wound care", "Patient education", "Documentation", "Injections", "Catheter care", "Vital signs", "G-tube care"] },
  { slug: "cna",       name: "CNA / PCT Resume",         bestFor: "Patient care techs + nursing assistants.",
    sections: ["ADLs", "Vitals", "Mobility", "Bedside care", "Transport"],
    skills: ["ADLs", "Vital signs", "Patient transport", "Bedside care", "Mobility support", "Safety checks", "Specimen collection", "EKG (12-lead)"] },
  { slug: "allied",    name: "Allied Health Resume",     bestFor: "Respiratory, surgical tech, sterile processing, lab, pharmacy, dialysis, behavioral.",
    sections: ["Discipline", "Modality / Procedures", "Compliance", "EMR"],
    skills: ["Sterile technique", "Documentation", "Equipment QC", "Infection control", "Patient handoff", "Team collaboration"] },
  { slug: "imaging",   name: "Imaging / Radiology",      bestFor: "ARRT / ARDMS — CT, MRI, X-Ray, Ultrasound, Echo.",
    sections: ["Modality", "ARRT/ARDMS", "PACS", "Trauma", "Contrast"],
    skills: ["ARRT", "PACS", "Contrast administration", "Trauma imaging", "CT", "MRI", "Ultrasound", "X-Ray", "Echo", "Patient positioning"] },
  { slug: "therapy",   name: "Therapy Resume",           bestFor: "PT / OT / SLP across outpatient, SNF, peds, rehab.",
    sections: ["Discipline", "Population", "Productivity", "Documentation"],
    skills: ["Plan of care", "Functional outcomes", "Productivity metrics", "Manual therapy", "Documentation (EMR)", "Family education", "Interdisciplinary care"] },
  { slug: "np",        name: "Nurse Practitioner",       bestFor: "NPs — primary, urgent, specialty.",
    sections: ["DEA / NPI", "Collaborative agreements", "Patient volume", "Procedures", "EMR"],
    skills: ["Differential diagnosis", "Prescribing", "Suturing", "Joint injections", "Chronic disease mgmt", "Wellness visits", "Procedure coding"] },
  { slug: "physician", name: "Physician Resume",         bestFor: "MD / DO — hospital, clinic, fellowship.",
    sections: ["Board cert", "Privileges", "Residency / Fellowship", "Procedures"],
    skills: ["Inpatient management", "Procedural skills", "Teaching", "Quality improvement", "Multidisciplinary leadership", "Evidence-based practice"] },
  { slug: "executive", name: "Executive Healthcare Leadership", bestFor: "Director / VP / CNO / CMO — system, hospital, or service-line leadership.",
    sections: ["Leadership scope", "Budgets", "Staff led", "Quality outcomes", "Compliance"],
    skills: ["Operational leadership", "Budget P&L", "Quality outcomes", "Joint Commission readiness", "Staff development", "Strategic planning", "Board reporting"] },
  { slug: "msp_compact", name: "Compact MSP/VMS Submission", bestFor: "Single-page recruiter submission resume — ATS-tight, no fluff.",
    sections: ["License-first header", "Compact summary", "Recent roles only", "Certs", "EMR"],
    skills: ["License-first formatting", "Single-page output", "Recent roles emphasis", "Cert-front loading", "EMR clarity", "MSP-ready"] },
];

const EMR_OPTIONS = ["Epic", "Cerner", "Meditech", "Allscripts", "Athena", "eClinicalWorks", "NextGen", "PointClickCare", "Other"];
const COMMON_CERTS = ["BLS", "ACLS", "PALS", "NIHSS", "TNCC", "CPI", "CCRN", "CNOR", "ARRT", "ARDMS", "NBRC", "AANP/ANCC"];
const ASSIGNMENT_TYPES = ["Travel", "Local", "PRN", "Permanent"];
const SHIFT_OPTIONS = ["Days", "Nights", "Mids", "Rotating", "Flexible"];

const EMPTY_FIELDS = {
  template: "rn",
  // Header
  fullName: "", title: "", city: "", state: "", phone: "", email: "", linkedin: "", licenseSummary: "",
  // Summary
  yearsExp: "", specialty: "", settings: "", strengths: "", summary: "",
  // Skills
  skills: "",
  // Licenses & certs
  licenses: "", certifications: "",
  // Work
  workExperience: "",
  // Education
  education: "",
  // EMR
  emr: [],
  // Specialty-specific extras (only the relevant subset rendered per template)
  specialty_extras: {
    chargePreceptor: false, travelExperience: false, patientRatios: "", unitSpecialties: "", floating: "",
    ltcSnfRehab: "", medPass: false, woundCare: false, injections: false, patientEducation: false,
    adls: false, vitals: false, mobility: false, transport: false, safetyChecks: false, specimens: false, ekg12: false,
    discipline: "", modality: "", arrt: "", ardms: "", pacs: false, contrast: false, trauma: false, equipment: "",
    therapyDiscipline: "", population: "", productivity: "", documentation: "",
    dea: "", npi: "", collaborative: "", procedures: "", patientVolume: "",
    boardCert: "", fellowship: "", residency: "", privileges: "", malpracticeNote: "", stateLicenses: "",
  },
  // Preferences
  assignmentTypes: [],
  shiftPreference: "",
  desiredLocation: "",
  // Additional
  additional: "",
};

const SAMPLE_FIELDS = {
  ...EMPTY_FIELDS,
  template: "rn",
  fullName: "Jane M. Doe", title: "Registered Nurse — ICU / Critical Care",
  city: "Houston", state: "TX", phone: "(555) 555-1234", email: "jane.doe@example.com",
  linkedin: "linkedin.com/in/janedoe-rn",
  licenseSummary: "RN — TX (Compact), CA",
  yearsExp: "8", specialty: "ICU / Critical Care", settings: "Level-I trauma center, surgical ICU",
  strengths: "vasoactive drips, CRRT, charge nurse, interdisciplinary collaboration",
  summary: "Critical-care RN with 8+ years of experience at Level-I trauma centers, including 3 years as charge nurse and unit preceptor. Skilled in vasoactive drip titration, CRRT, post-cardiac surgery recovery, and rapid-response team coordination.",
  skills: "Vasoactive drips, CRRT, Swan-Ganz, IABP, ACLS, PALS, EKG interpretation, Charge nurse, Preceptor, Interdisciplinary rounds, Family communication, Telemetry monitoring",
  licenses: "RN — Texas (Compact), Active, expires 12/2026\nRN — California, Active, expires 03/2027",
  certifications: "BLS, ACLS, PALS, CCRN, TNCC, NIHSS",
  workExperience:
    "Memorial Hermann TMC — Charge RN, ICU | Houston, TX | 06/2020 – Present\n" +
    "- Lead 18-bed surgical ICU with 1:2 ratio; trauma + post-cardiac surgery population.\n" +
    "- Precept new graduate nurses; coordinate interdisciplinary rounds.\n" +
    "- Manage CRRT, IABP, Swan-Ganz, vasoactive drips daily.\n\n" +
    "HCA Methodist — Staff RN, Surgical ICU | Houston, TX | 05/2017 – 06/2020\n" +
    "- 1:2 surgical ICU ratio; post-CABG and abdominal surgery recovery.\n" +
    "- Float to PACU and step-down as needed.",
  education: "BSN — University of Texas at Austin, 2017",
  emr: ["Epic", "Cerner"],
  specialty_extras: {
    ...EMPTY_FIELDS.specialty_extras,
    chargePreceptor: true, travelExperience: false, patientRatios: "1:2 ICU",
    unitSpecialties: "Surgical ICU, post-CABG, trauma",
  },
  assignmentTypes: ["Travel", "Local"],
  shiftPreference: "Nights",
  desiredLocation: "Texas, Florida, California",
  additional: "Bilingual (English / Spanish). Volunteer ACLS instructor.",
};

/* ===================================================================== */
/*  Resume rendering helpers                                             */
/* ===================================================================== */

/**
 * Parse an AI-formatted or rule-based resume text (with `# SECTION` markers)
 * into our structured `fields` shape. Used after upload + AI format so the
 * manual form and live preview reflect the uploaded resume — NOT the sample.
 *
 * Conservative: only fills fields when high-confidence; never overwrites
 * non-empty existing values.
 */
function parseFormattedToFields(text, baseFields = {}) {
  const out = { ...baseFields };
  if (!text || typeof text !== "string") return out;
  const lines = text.split("\n");
  const sections = { _pre: [] };
  let current = "_pre";
  for (const raw of lines) {
    const line = raw.replace(/\s+$/g, "");
    if (line.startsWith("# ")) {
      current = line.replace(/^#\s+/, "").trim().toUpperCase();
      sections[current] = [];
    } else {
      (sections[current] = sections[current] || []).push(line);
    }
  }
  const sec = (k) => (sections[k] || []).join("\n").trim();
  const secLines = (k) => (sections[k] || []).filter((l) => l.trim());
  const setIfEmpty = (k, v) => { if (v && !(out[k] || "").trim()) out[k] = v; };

  // --- HEADER ---
  const head = secLines("HEADER");
  if (head[0]) setIfEmpty("fullName", head[0].replace(/^[#\-•\s]+/, "").trim());
  if (head[1] && !/[|,@]/.test(head[1])) setIfEmpty("title", head[1].trim());

  // contact line(s)
  for (const line of head) {
    const parts = line.split("|").map((s) => s.trim()).filter(Boolean);
    for (const p of parts) {
      if (/@/.test(p)) setIfEmpty("email", p);
      else if (/linkedin\./i.test(p)) setIfEmpty("linkedin", p);
      else if (/^[\d()+\-.\s]{7,}$/.test(p)) setIfEmpty("phone", p);
      else if (/,/.test(p) && p.length < 40 && !/^licenses?:/i.test(p)) {
        const [city, state] = p.split(",").map((s) => s.trim());
        if (city && !out.city) out.city = city;
        if (state && !out.state) out.state = state.toUpperCase().slice(0, 2);
      } else if (/^licenses?:/i.test(p)) {
        setIfEmpty("licenseSummary", p.replace(/^licenses?:\s*/i, "").trim());
      }
    }
    if (/^licenses?:/i.test(line)) {
      setIfEmpty("licenseSummary", line.replace(/^licenses?:\s*/i, "").trim());
    }
  }

  // --- SUMMARY ---
  setIfEmpty("summary", sec("PROFESSIONAL SUMMARY") || sec("SUMMARY"));

  // --- SKILLS ---
  const skillsLines = secLines("CORE SKILLS").concat(secLines("SKILLS"));
  if (skillsLines.length) {
    const flat = skillsLines.map((l) => l.replace(/^[-•\s]+/, "").trim()).filter(Boolean).join(", ");
    setIfEmpty("skills", flat);
  }

  // --- LICENSES & CERTIFICATIONS ---
  const licLines = secLines("LICENSES & CERTIFICATIONS").concat(secLines("LICENSES"), secLines("CERTIFICATIONS"));
  const certLine = licLines.find((l) => /^certifications?:/i.test(l));
  const licOnly = licLines
    .filter((l) => !/^certifications?:/i.test(l))
    .map((l) => l.replace(/^[-•\s]+/, "").trim())
    .filter(Boolean);
  if (licOnly.length) setIfEmpty("licenses", licOnly.join("\n"));
  if (certLine) setIfEmpty("certifications", certLine.replace(/^certifications?:\s*/i, "").trim());

  // --- WORK EXPERIENCE ---
  setIfEmpty("workExperience", sec("WORK EXPERIENCE") || sec("PROFESSIONAL EXPERIENCE") || sec("EXPERIENCE"));

  // --- EDUCATION ---
  setIfEmpty("education", sec("EDUCATION"));

  // --- EMR ---
  const emrText = sec("EMR SYSTEMS") || sec("EMR");
  if (emrText && (!Array.isArray(out.emr) || out.emr.length === 0)) {
    out.emr = emrText.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
  }

  // --- ADDITIONAL ---
  setIfEmpty("additional", sec("ADDITIONAL"));

  return out;
}

function buildLocalResume(f) {
  const parts = [];
  parts.push("# HEADER");
  parts.push((f.fullName || "[Missing: Full Name]").toUpperCase());
  if (f.title) parts.push(f.title);
  const loc = [f.city, f.state].filter(Boolean).join(", ");
  parts.push([loc, f.phone, f.email, f.linkedin].filter(Boolean).join(" | "));
  if (f.licenseSummary) parts.push(`Licenses: ${f.licenseSummary}`);
  parts.push("");

  parts.push("# PROFESSIONAL SUMMARY");
  parts.push(
    f.summary?.trim() ||
      (f.yearsExp && f.specialty
        ? `${f.title || "Licensed clinician"} with ${f.yearsExp}+ years of experience in ${f.specialty}. ${f.settings ? `Recent settings include ${f.settings}.` : ""} ${f.strengths ? `Key strengths: ${f.strengths}.` : ""}`.trim()
        : "[Missing: Add 2-4 line professional summary.]"),
  );
  parts.push("");

  parts.push("# CORE SKILLS");
  const skills = (f.skills || "").split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
  if (skills.length === 0) parts.push("- [Missing: add 6–10 core skills]");
  else parts.push(skills.map((s) => `- ${s}`).join("\n"));
  parts.push("");

  parts.push("# LICENSES & CERTIFICATIONS");
  parts.push(`Licenses: ${f.licenses || "[Missing — required for MSP/VMS]"}`);
  if (f.certifications) parts.push(`Certifications: ${f.certifications}`);
  parts.push("");

  parts.push("# WORK EXPERIENCE");
  parts.push(f.workExperience?.trim() || "[Missing: add at least 2 recent roles with dates + bullets]");
  parts.push("");

  parts.push("# EDUCATION");
  parts.push(f.education || "[Missing: degree, school]");
  parts.push("");

  parts.push("# EMR SYSTEMS");
  const emrLine = Array.isArray(f.emr) ? f.emr.join(", ") : (f.emr || "");
  parts.push(emrLine || "[Missing: list EMRs — Epic / Cerner / Meditech]");

  // Specialty extras → readable narrative
  const ex = f.specialty_extras || {};
  const extras = [];
  if (f.template === "rn") {
    if (ex.unitSpecialties) extras.push(`Unit specialties: ${ex.unitSpecialties}`);
    if (ex.patientRatios) extras.push(`Patient ratios: ${ex.patientRatios}`);
    if (ex.chargePreceptor) extras.push("Charge / Preceptor experience");
    if (ex.travelExperience) extras.push("Travel assignment experience");
    if (ex.floating) extras.push(`Floating: ${ex.floating}`);
  } else if (f.template === "lpn") {
    if (ex.ltcSnfRehab) extras.push(`Settings: ${ex.ltcSnfRehab}`);
    if (ex.medPass) extras.push("Medication administration");
    if (ex.woundCare) extras.push("Wound care");
    if (ex.injections) extras.push("Injections");
    if (ex.patientEducation) extras.push("Patient education");
  } else if (f.template === "cna") {
    if (ex.adls) extras.push("ADLs"); if (ex.vitals) extras.push("Vital signs");
    if (ex.mobility) extras.push("Mobility support"); if (ex.transport) extras.push("Patient transport");
    if (ex.safetyChecks) extras.push("Safety checks"); if (ex.specimens) extras.push("Specimen collection");
    if (ex.ekg12) extras.push("12-lead EKG");
  } else if (f.template === "allied") {
    if (ex.discipline) extras.push(`Discipline: ${ex.discipline}`);
    if (ex.modality) extras.push(`Modality / Procedures: ${ex.modality}`);
  } else if (f.template === "imaging") {
    if (ex.modality) extras.push(`Modality: ${ex.modality}`);
    if (ex.arrt) extras.push(`ARRT: ${ex.arrt}`);
    if (ex.ardms) extras.push(`ARDMS: ${ex.ardms}`);
    if (ex.pacs) extras.push("PACS");
    if (ex.contrast) extras.push("Contrast administration");
    if (ex.trauma) extras.push("Trauma imaging");
    if (ex.equipment) extras.push(`Equipment: ${ex.equipment}`);
  } else if (f.template === "therapy") {
    if (ex.therapyDiscipline) extras.push(`Discipline: ${ex.therapyDiscipline}`);
    if (ex.population) extras.push(`Population: ${ex.population}`);
    if (ex.productivity) extras.push(`Productivity: ${ex.productivity}`);
    if (ex.documentation) extras.push(`Documentation: ${ex.documentation}`);
  } else if (f.template === "np") {
    if (ex.dea) extras.push(`DEA: ${ex.dea}`);
    if (ex.npi) extras.push(`NPI: ${ex.npi}`);
    if (ex.collaborative) extras.push(`Collaborative agreements: ${ex.collaborative}`);
    if (ex.procedures) extras.push(`Procedures: ${ex.procedures}`);
    if (ex.patientVolume) extras.push(`Patient volume: ${ex.patientVolume}`);
  } else if (f.template === "physician") {
    if (ex.boardCert) extras.push(`Board certification: ${ex.boardCert}`);
    if (ex.residency) extras.push(`Residency: ${ex.residency}`);
    if (ex.fellowship) extras.push(`Fellowship: ${ex.fellowship}`);
    if (ex.privileges) extras.push(`Privileges: ${ex.privileges}`);
    if (ex.procedures) extras.push(`Procedures: ${ex.procedures}`);
    if (ex.patientVolume) extras.push(`Patient volume: ${ex.patientVolume}`);
    if (ex.stateLicenses) extras.push(`State licenses: ${ex.stateLicenses}`);
  }
  if (extras.length) {
    parts.push("");
    parts.push("# SPECIALTY DETAILS");
    parts.push(extras.map((e) => `- ${e}`).join("\n"));
  }

  // Preferences
  const prefs = [];
  if (Array.isArray(f.assignmentTypes) && f.assignmentTypes.length)
    prefs.push(`Assignment types: ${f.assignmentTypes.join(", ")}`);
  if (f.shiftPreference) prefs.push(`Shift preference: ${f.shiftPreference}`);
  if (f.desiredLocation) prefs.push(`Desired location: ${f.desiredLocation}`);
  if (prefs.length) {
    parts.push("");
    parts.push("# PREFERENCES");
    parts.push(prefs.map((p) => `- ${p}`).join("\n"));
  }

  if (f.additional?.trim()) {
    parts.push("");
    parts.push("# ADDITIONAL");
    parts.push(f.additional);
  }
  return parts.join("\n");
}

/* ===================================================================== */
/*  ATS scoring (frontend mirror — same heuristics as backend)           */
/* ===================================================================== */

const SPECIALTY_RX = /\b(icu|med[-\s]?surg|er|emergency|tele(?:metry)?|l&d|labor|delivery|pacu|or|oncology|neuro|cardiac|peds|nicu|critical care|pcu|stepdown|cvicu|sicu|orthopedic|behavioral|psych|hospice|home health|primary care|family practice|radiology|ct|mri|x[-\s]?ray|ultrasound|echo|sterile processing|surgical tech|respiratory|pulmonary|dialysis|wound care|long[-\s]?term care|ltc|snf|rehab)\b/i;
const EMR_RX = /\b(epic|cerner|meditech|allscripts|athena|eclinical|nextgen|powerchart|sunrise|paragon|pointclickcare)\b/i;
const DATE_RX = /(0?[1-9]|1[0-2])[\s\-/]+(19|20)\d{2}|(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\s+(19|20)\d{2}|(19|20)\d{2}\s*[-–to]+\s*(present|current|(19|20)\d{2})/i;
const EMAIL_RX = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const PHONE_RX = /\+?\d[\d\s().\-]{8,}\d/;
const FAC_RX = /\b(hospital|medical center|clinic|health(?:care)?|memorial|methodist|community|st\.?\s|saint)\b/i;
const TITLE_RX = /\b(rn|lpn|lvn|cna|pct|np|crna|md|do|pa|pt|ot|slp|tech|technician|therapist|nurse)\b/i;

function scoreResume(text) {
  const t = text || "";
  const tl = t.toLowerCase();
  const checks = [
    ["Phone number present",            PHONE_RX.test(t),                 8,  "Add a working phone number."],
    ["Email present",                   EMAIL_RX.test(t),                 8,  "Add a professional email address."],
    ["License section",                 /license|rn |lpn |nurse|compact/i.test(t), 10, "Include licenses with state(s) + compact status."],
    ["Certifications",                  /\b(bls|acls|pals|ccrn|certified|certification)\b/i.test(t), 8, "List BLS / ACLS / PALS / CCRN, etc."],
    ["Employment dates",                DATE_RX.test(t),                  10, "Include employment dates (Month YYYY – Present)."],
    ["Facility names",                  FAC_RX.test(t),                   8,  "Add facility/employer names."],
    ["Specialty keywords",              SPECIALTY_RX.test(t),             10, "Mention specialty clearly (ICU, ER, Med-Surg…)."],
    ["EMR systems",                     EMR_RX.test(t),                   8,  "List EMRs (Epic / Cerner / Meditech…)."],
    ["Clear professional title",        TITLE_RX.test(t),                 6,  "Add clear professional title (e.g. 'RN — ICU')."],
    ["Professional Summary section",    /summary/i.test(t),               8,  "Add a 2-4 line Professional Summary."],
    ["Skills section",                  /skills/i.test(tl),               8,  "Add a Core Skills section."],
    ["Length appropriate (≤6000 chars)", t.length <= 6000,                8,  "Trim resume — overly long resumes hurt ATS parsing."],
  ];
  const earned = checks.reduce((s, [, ok, w]) => s + (ok ? w : 0), 0);
  const total = checks.reduce((s, [, , w]) => s + w, 0);
  const score = total ? Math.round((earned / total) * 100) : 0;
  let tone, readiness;
  if (score >= 90)      { tone = "green";    readiness = "MSP Ready"; }
  else if (score >= 75) { tone = "green";    readiness = "MSP Ready with Minor Fixes"; }
  else if (score >= 60) { tone = "yellow";   readiness = "Needs Work"; }
  else if (score >= 40) { tone = "red";      readiness = "Not Ready"; }
  else                  { tone = "critical"; readiness = "Major Gaps"; }
  return {
    score, tone, readiness,
    checks: checks.map(([label, ok, weight, fix]) => ({ label, ok, weight, fix })),
    missing: checks.filter(([, ok]) => !ok).map(([, , , fix]) => fix),
  };
}

const TONE_STYLES = {
  green:    { bg: "bg-emerald-50",  border: "border-emerald-200", pill: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  yellow:   { bg: "bg-amber-50",    border: "border-amber-200",   pill: "bg-amber-100 text-amber-800 border-amber-200" },
  red:      { bg: "bg-rose-50",     border: "border-rose-200",    pill: "bg-rose-100 text-rose-800 border-rose-200" },
  critical: { bg: "bg-rose-100",    border: "border-rose-300",    pill: "bg-rose-600 text-white border-rose-700" },
};

/* ===================================================================== */
/*  Component                                                            */
/* ===================================================================== */

const STORAGE_KEY = "trital_resume_library_v2";

const METHOD_CARDS = [
  { id: "score",    title: "Quick Score™",    icon: Gauge,      desc: "Recruiter mode — paste any resume, get an instant 12-point ATS score.", testId: "method-score" },
  { id: "upload",   title: "Upload Resume",   icon: Upload,     desc: "PDF, DOCX, or TXT. We extract text and reformat ATS-safe.",          testId: "method-upload" },
  { id: "manual",   title: "Build Manually",  icon: FileText,   desc: "Fill structured fields. Live preview + ATS score.",                  testId: "method-manual" },
  { id: "template", title: "Choose Template", icon: LayoutGrid, desc: "12 TRITAL-owned healthcare templates — RN to Physician to Executive.",   testId: "method-template" },
  { id: "ai",       title: "AI Assist",       icon: Sparkles,   desc: "AI reformats your content. Rule-based fallback included.",            testId: "method-ai" },
];

const HERO_STATS = [
  { label: "Healthcare Templates",   value: "12",  sub: "RN → Physician → Executive", icon: LayoutGrid },
  { label: "ATS Score™ Checks",      value: "12",  sub: "Deterministic, instant",     icon: Gauge },
  { label: "Export Formats",         value: "PDF · DOCX · TXT", sub: "+ Print to PDF", icon: FileDown },
  { label: "Recruiter Cover Sheet",  value: "Optional", sub: "MSP/VMS submission ready", icon: Briefcase },
];

export default function ResumeFormatter() {
  const [mode, setMode] = useState("manual");
  const [fields, setFields] = useState(() => {
    try { const raw = localStorage.getItem(STORAGE_KEY); if (raw) return { ...EMPTY_FIELDS, ...JSON.parse(raw) }; } catch {}
    return EMPTY_FIELDS;
  });
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiProvider, setAiProvider] = useState("");
  const [aiWarning, setAiWarning] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadedName, setUploadedName] = useState("");
  const [downloadBusy, setDownloadBusy] = useState(null); // 'pdf' | 'docx' | null
  const fileInputRef = useRef(null);

  // Quick Score / specialty-detect state
  const [scoreText, setScoreText] = useState("");
  const [detectedSpecialty, setDetectedSpecialty] = useState(null); // {template, confidence}

  // Preview mode — ATS plain vs Branded PDF view
  const [previewMode, setPreviewMode] = useState("branded"); // 'ats' | 'branded'

  // Recruiter cover sheet state
  const [coverSheetOpen, setCoverSheetOpen] = useState(false);
  const [includeCoverSheet, setIncludeCoverSheet] = useState(false);
  const [coverSheet, setCoverSheet] = useState({
    recruiterName: "", recruiterEmail: "", recruiterPhone: "",
    roleSubmittedFor: "", client: "", startDate: "", payRange: "", recruiterNote: "",
  });

  useEffect(() => {
    document.title = "TRITAL Resume Library™ — Free ATS-Safe Healthcare Resume Builder | Workforce OS™";
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(fields)); } catch {}
    }, 400);
    return () => clearTimeout(t);
  }, [fields]);

  function up(k, v) { setFields((p) => ({ ...p, [k]: v })); }
  function upExtras(k, v) { setFields((p) => ({ ...p, specialty_extras: { ...(p.specialty_extras || {}), [k]: v } })); }

  const resumeText = useMemo(() => (aiText || buildLocalResume(fields)), [fields, aiText]);
  const ats = useMemo(() => scoreResume(resumeText), [resumeText]);
  const tone = TONE_STYLES[ats.tone];
  const currentTemplate = TEMPLATES.find((t) => t.slug === fields.template) || TEMPLATES[0];

  // Empty state — when no data of any kind has been entered/uploaded/loaded,
  // show a polished sample preview so users immediately see what the output
  // looks like. As soon as ANY content arrives (upload, AI text, or a typed
  // field), we drop the sample and reflect the user's actual data.
  const isEmptyState = !fields.fullName && !fields.title && !fields.skills &&
                       !fields.workExperience && !fields.education && !aiText;
  const previewFields = useMemo(() => {
    if (isEmptyState) return SAMPLE_FIELDS;
    // If aiText is set but structured fields haven't caught up yet (e.g. right
    // after upload), parse on the fly so the preview is never blank.
    if (aiText && !fields.fullName) return parseFormattedToFields(aiText, fields);
    return fields;
  }, [isEmptyState, fields, aiText]);

  /* ---------- Actions ---------- */

  function loadSample() { setFields(SAMPLE_FIELDS); setAiText(""); toast.success("Sample resume loaded."); }
  function reset()      { setFields(EMPTY_FIELDS); setAiText(""); try { localStorage.removeItem(STORAGE_KEY); } catch {}; toast.success("Reset."); }
  function copyResume() { navigator.clipboard.writeText(resumeText); toast.success("Resume copied to clipboard."); }
  function downloadTxt() {
    const blob = new Blob([resumeText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(fields.fullName || "resume").replace(/\s+/g, "_")}_TRITAL_Resume.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("TXT downloaded.");
  }
  function printResume() {
    document.body.classList.add("resume-print-mode");
    window.print();
    setTimeout(() => document.body.classList.remove("resume-print-mode"), 500);
  }

  async function downloadFile(kind /* 'pdf' | 'docx' */) {
    // Sample-only state guard — block confusing blank downloads.
    if (isEmptyState) {
      toast.error("Please upload a resume or fill the form before downloading.");
      return;
    }
    // Single source of truth: the `previewFields` object reflects exactly
    // what the Preview pane is rendering (post-upload, post-AI, post-edit).
    // Send the same shape so the downloaded file matches the preview.
    setDownloadBusy(kind);
    try {
      const body = {
        fields: previewFields,
        formatted: aiText || "",
        filename: previewFields.fullName || "TRITAL_Resume",
      };
      if (kind === "pdf" && includeCoverSheet) {
        body.cover_sheet = coverSheet;
      }
      const res = await fetch(`/api/resume/${kind}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(previewFields.fullName || "TRITAL_Resume").replace(/\s+/g, "_")}_TRITAL_Resume.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} downloaded.`);
    } catch {
      toast.error(`${kind.toUpperCase()} generation failed — try Print/PDF as fallback.`);
    } finally {
      setDownloadBusy(null);
    }
  }

  async function handleUpload(file) {
    if (!file) return;
    setUploadBusy(true);
    setUploadedName(file.name);
    setAiWarning("");
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`/api/resume/parse-upload`, { method: "POST", body: form });
      const data = await res.json();
      if (data?.error) {
        toast.error(data.error);
        return;
      }
      const rawText = (data?.text || "").trim();
      if (!rawText) {
        toast.error("We could not fully read this resume. Please copy/paste the text into AI Assist or use the manual builder.");
        return;
      }
      setAiText(rawText);

      // Auto-format and populate structured fields so the preview reflects
      // the UPLOADED resume — not the sample data.
      let formatted = rawText;
      let provider = "rule_based";
      try {
        const fmtRes = await fetch(`/api/ai/resume-format`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ raw_text: rawText, fields: {}, template: fields.template }),
        });
        if (fmtRes.ok) {
          const fmt = await fmtRes.json();
          formatted = fmt.formatted || rawText;
          provider = fmt.provider || provider;
          setAiText(formatted);
          setAiProvider(provider);
          if (fmt.warning) setAiWarning(fmt.warning);
        }
      } catch { /* rule-based fallback below */ }

      // Detect specialty (best-effort)
      try {
        const det = await fetch(`/api/resume/detect-specialty`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text: rawText }),
        });
        if (det.ok) {
          const d = await det.json();
          if (d?.template) {
            setDetectedSpecialty({ template: d.template, confidence: d.confidence });
            // auto-switch template only if user hasn't overridden
            if (!fields.fullName) up("template", d.template);
          }
        }
      } catch { /* ignore */ }

      // Parse formatted text → fields (preserves any existing user edits)
      setFields((prev) => parseFormattedToFields(formatted, prev));

      toast.success(`Parsed and loaded — review & edit fields, then download.`);
      setMode("manual");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploadBusy(false);
    }
  }

  async function runAiFormat() {
    if (!aiText.trim() && !fields.fullName) {
      toast.error("Add resume text or fill the manual form first.");
      return;
    }
    setAiBusy(true);
    setAiWarning("");
    try {
      const res = await fetch(`/api/ai/resume-format`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ raw_text: aiText, fields, template: fields.template }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAiText(data.formatted || "");
      setAiProvider(data.provider || "");
      // Sync structured fields from the AI-formatted text so the preview +
      // download reflect the AI result, not the previous form state.
      if (data.formatted) {
        setFields((prev) => parseFormattedToFields(data.formatted, prev));
      }
      if (data.warning) {
        setAiWarning(data.warning);
        toast.warning(data.warning);
      } else {
        toast.success(`Formatted by ${data.provider === "rule_based" ? "rule-based engine" : `AI (${data.provider})`}`);
      }
    } catch {
      toast.error("AI formatting failed — using rule-based draft.");
      setAiText(buildLocalResume(fields));
    } finally {
      setAiBusy(false);
    }
  }

  // Quick Score — instant ATS score for any pasted resume text (recruiter triage)
  const quickScoreAts = useMemo(() => scoreResume(scoreText || ""), [scoreText]);
  const quickTone = TONE_STYLES[quickScoreAts.tone];

  async function detectSpecialty(text) {
    if (!text || text.trim().length < 30) {
      setDetectedSpecialty(null);
      return;
    }
    try {
      const res = await fetch(`/api/resume/detect-specialty`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.template) setDetectedSpecialty({ template: data.template, confidence: data.confidence });
      else setDetectedSpecialty(null);
    } catch { /* ignore */ }
  }

  // Debounced auto-detect as user pastes into Quick Score or AI textarea
  useEffect(() => {
    const t = setTimeout(() => detectSpecialty(scoreText), 600);
    return () => clearTimeout(t);
  }, [scoreText]);
  useEffect(() => {
    if (mode !== "ai") return;
    const t = setTimeout(() => detectSpecialty(aiText), 600);
    return () => clearTimeout(t);
  }, [aiText, mode]);

  function toggleListItem(key, value) {
    const cur = Array.isArray(fields[key]) ? fields[key] : [];
    up(key, cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value]);
  }

  function appendCsv(key, value) {
    const cur = fields[key] || "";
    if (cur.toLowerCase().includes(String(value).toLowerCase())) return;
    up(key, cur ? `${cur}, ${value}` : value);
  }

  /* ---------- Render ---------- */

  return (
    <WorkforceOsLayout
      breadcrumb="Resume Library"
      title="Healthcare Resume Formatter"
      description="Create ATS-safe, MSP-ready healthcare resumes for nursing, allied, therapy, APP, and physician roles. Upload, build, or pick a template — free, no signup, no payment."
      showAiDisclaimer={true}
    >
      <section className="py-8" data-testid="resume-formatter-page">
        <div className="max-w-[1300px] mx-auto px-5 lg:px-8">
          {/* Free badge row */}
          <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="resume-badges">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 text-[11px] font-bold uppercase tracking-wider">Free Tool</span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200 text-[11px] font-bold uppercase tracking-wider">ATS-Safe</span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-trital-medical-blue-soft text-trital-navy border border-trital-navy/20 text-[11px] font-bold uppercase tracking-wider">MSP/VMS Ready</span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-100 text-rose-800 border border-rose-200 text-[11px] font-bold uppercase tracking-wider">Recruiter Mode</span>
            <span className="ml-auto text-[12px] text-slate-500">No payment · No signup · We don't collect SSN, DOB, full address, passport, bank, or sensitive medical data.</span>
          </div>

          {/* Hero stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="resume-hero-stats">
            {HERO_STATS.map(({ label, value, sub, icon: Icon }) => (
              <div key={label} className="rounded-2xl bg-gradient-to-br from-trital-navy to-[#0A1A55] text-white p-4 shadow-sm" data-testid={`hero-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="flex items-center justify-between">
                  <Icon className="w-4 h-4 opacity-80" />
                  <span className="text-[10px] uppercase tracking-wider text-white/60">TRITAL</span>
                </div>
                <div className="mt-2 font-display font-black text-2xl tabular-nums leading-tight">{value}</div>
                <div className="text-[11.5px] font-semibold text-white/85 leading-tight">{label}</div>
                <div className="text-[10.5px] text-white/55 leading-tight mt-0.5">{sub}</div>
              </div>
            ))}
          </div>

          {/* Method selector — 5 prominent cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="resume-methods">
            {METHOD_CARDS.map(({ id, title, icon: Icon, desc, testId }) => {
              const active = mode === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setMode(id)}
                  className={`text-left rounded-2xl border p-4 transition-all ${
                    active
                      ? "bg-trital-navy text-white border-trital-navy shadow-md"
                      : "bg-white text-trital-navy border-slate-200 hover:border-trital-navy"
                  }`}
                  data-testid={testId}
                >
                  <div className="flex items-center justify-between">
                    <Icon className="w-5 h-5" />
                    {active && <CheckCircle2 className="w-4 h-4" />}
                  </div>
                  <div className="mt-2 font-display font-bold text-[15px]">{title}</div>
                  <div className={`mt-0.5 text-[11.5px] leading-snug ${active ? "text-white/80" : "text-slate-500"}`}>{desc}</div>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_460px] gap-6">
            {/* ---------- LEFT panel ---------- */}
            <div className="space-y-4">
              {/* QUICK SCORE — recruiter mass-screening */}
              {mode === "score" && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="score-panel">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center shrink-0">
                      <Gauge className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h2 className="font-display font-bold text-lg text-trital-navy">Quick Score™ — recruiter triage</h2>
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-rose-600 text-white font-bold">New</span>
                      </div>
                      <p className="mt-1 text-[13px] text-slate-600">Paste any healthcare resume — get an instant 12-point ATS score + missing-items breakdown. Built for recruiters screening 100+ resumes / week.</p>
                    </div>
                  </div>

                  <textarea
                    rows={14}
                    value={scoreText}
                    onChange={(e) => setScoreText(e.target.value)}
                    placeholder="Paste candidate resume text here — Quick Score runs instantly, no signup required…"
                    className="mt-4 w-full px-3 py-2 rounded-lg border border-slate-300 text-[12.5px] font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-trital-navy/30"
                    data-testid="score-text"
                  />

                  {detectedSpecialty && (
                    <div className="mt-3 rounded-lg bg-trital-medical-blue-soft border border-trital-navy/20 p-3 flex items-center justify-between gap-3" data-testid="specialty-suggestion">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-trital-navy shrink-0" />
                        <span className="text-[12.5px] text-trital-navy">
                          <b>Detected specialty:</b> {(TEMPLATES.find(t => t.slug === detectedSpecialty.template) || {}).name || detectedSpecialty.template}
                          <span className="ml-2 text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white border border-trital-navy/30 text-trital-navy font-bold">{detectedSpecialty.confidence} confidence</span>
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => { up("template", detectedSpecialty.template); setAiText(scoreText); setMode("ai"); toast.success(`Loaded into ${(TEMPLATES.find(t => t.slug === detectedSpecialty.template) || {}).name || detectedSpecialty.template} — ready for AI format.`); }}
                        className="text-[11.5px] font-semibold text-white bg-trital-navy px-3 py-1 rounded-full"
                        data-testid="score-use-template"
                      >
                        Use this template →
                      </button>
                    </div>
                  )}

                  {scoreText.trim().length >= 50 && (
                    <div className={`mt-4 rounded-2xl border p-5 ${quickTone.bg} ${quickTone.border}`} data-testid="quick-score-card">
                      <div className="flex items-center justify-between">
                        <div className="overline text-slate-600">ATS Resume Score™</div>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold border ${quickTone.pill}`} data-testid="quick-score-readiness">{quickScoreAts.readiness}</span>
                      </div>
                      <div className="mt-1 flex items-baseline gap-2">
                        <div className="font-display font-black text-5xl tabular-nums text-trital-navy" data-testid="quick-score-value">{quickScoreAts.score}</div>
                        <div className="text-trital-navy/60 font-semibold">/ 100</div>
                      </div>
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                        {quickScoreAts.checks.map((c, i) => (
                          <div key={i} className="flex items-start gap-2 text-[12px]">
                            {c.ok ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-rose-500 shrink-0" />}
                            <span className={c.ok ? "text-slate-700" : "text-slate-500"}>{c.label}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button type="button" onClick={() => { setAiText(scoreText); setMode("ai"); toast.success("Loaded into AI Assist — click 'Format resume' to reformat ATS-safe."); }} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-trital-navy text-white text-[12px] font-semibold" data-testid="score-send-ai">
                          <Wand2 className="w-3.5 h-3.5" /> Format ATS-safe
                        </button>
                        <button type="button" onClick={() => { navigator.clipboard.writeText(`ATS Score: ${quickScoreAts.score}/100 — ${quickScoreAts.readiness}\nMissing:\n${quickScoreAts.missing.map(m => `• ${m}`).join("\n")}`); toast.success("Score summary copied."); }} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-slate-300 text-[12px] text-trital-navy font-semibold" data-testid="score-copy-summary">
                          <Copy className="w-3.5 h-3.5" /> Copy score summary
                        </button>
                        <button type="button" onClick={() => { setScoreText(""); setDetectedSpecialty(null); }} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-slate-300 text-[12px] text-slate-600 font-semibold" data-testid="score-clear">
                          <RefreshCw className="w-3.5 h-3.5" /> Clear
                        </button>
                      </div>
                    </div>
                  )}

                  {scoreText.trim().length < 50 && (
                    <p className="mt-3 text-[11.5px] text-slate-500">Paste at least 50 characters of resume text to compute a score.</p>
                  )}
                </div>
              )}

              {/* UPLOAD */}
              {mode === "upload" && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="upload-panel">
                  <h2 className="font-display font-bold text-lg text-trital-navy">Upload your resume</h2>
                  <p className="mt-1 text-[13px] text-slate-600">PDF, DOCX, or TXT — max 4 MB. We extract the text, auto-format it ATS-safe, and load it into the editor so you can review & download.</p>
                  <ul className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11.5px] text-slate-700">
                    <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0" /> Text extracted in seconds</li>
                    <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0" /> Auto-loaded into Manual editor</li>
                    <li className="flex items-start gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0" /> Specialty auto-detected</li>
                  </ul>
                  <div className="mt-4">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.docx,.txt"
                      className="hidden"
                      onChange={(e) => handleUpload(e.target.files?.[0])}
                      data-testid="resume-upload-input"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadBusy}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-trital-navy text-white text-[13px] font-semibold disabled:opacity-50"
                      data-testid="resume-upload-btn"
                    >
                      {uploadBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
                      {uploadBusy ? "Parsing…" : "Select file"}
                    </button>
                    {uploadedName && <span className="ml-3 text-[12px] text-slate-500">Last: {uploadedName}</span>}
                  </div>
                  <p className="mt-4 text-[11.5px] text-slate-500">If parsing fails: copy/paste resume text into the AI Assist tab or use the manual builder.</p>
                </div>
              )}

              {/* TEMPLATE */}
              {mode === "template" && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="template-panel">
                  <h2 className="font-display font-bold text-lg text-trital-navy">TRITAL Resume Library™</h2>
                  <p className="mt-1 text-[13px] text-slate-600">Original TRITAL-owned templates. Pick one to pre-fill specialty-specific fields and skill suggestions.</p>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {TEMPLATES.map((t) => (
                      <button
                        key={t.slug}
                        type="button"
                        onClick={() => { up("template", t.slug); setMode("manual"); toast.success(`${t.name} template selected.`); }}
                        className={`text-left rounded-xl border p-4 transition-all ${
                          fields.template === t.slug
                            ? "border-trital-navy bg-trital-medical-blue-soft"
                            : "border-slate-200 bg-white hover:border-trital-navy"
                        }`}
                        data-testid={`template-card-${t.slug}`}
                      >
                        <div className="flex items-center justify-between">
                          <h3 className="font-display font-bold text-trital-navy text-[14.5px]">{t.name}</h3>
                          <div className="flex gap-1">
                            <span className="text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800 font-bold">ATS-Safe</span>
                            <span className="text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold">MSP-Ready</span>
                          </div>
                        </div>
                        <p className="mt-1 text-[12px] text-slate-600 leading-snug">{t.bestFor}</p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {t.sections.map((s) => (
                            <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{s}</span>
                          ))}
                        </div>
                        <span className="mt-2 inline-block text-[11.5px] font-semibold text-trital-navy">Use template →</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* AI ASSIST */}
              {mode === "ai" && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="ai-panel">
                  <h2 className="font-display font-bold text-lg text-trital-navy">AI Assist</h2>
                  <p className="mt-1 text-[13px] text-slate-600">Paste raw resume text or upload above — AI reformats into ATS-safe TRITAL structure. SambaNova primary → rule-based fallback. AI never invents licenses, certs, or experience.</p>
                  <textarea
                    rows={10}
                    value={aiText}
                    onChange={(e) => setAiText(e.target.value)}
                    placeholder="Paste raw resume text here…"
                    className="mt-3 w-full px-3 py-2 rounded-lg border border-slate-300 text-[12.5px] font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-trital-navy/30"
                    data-testid="ai-raw-text"
                  />
                  {detectedSpecialty && (
                    <div className="mt-3 rounded-lg bg-trital-medical-blue-soft border border-trital-navy/20 p-3 flex items-center justify-between gap-3" data-testid="ai-specialty-suggestion">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-trital-navy shrink-0" />
                        <span className="text-[12.5px] text-trital-navy">
                          <b>Detected specialty:</b> {(TEMPLATES.find(t => t.slug === detectedSpecialty.template) || {}).name || detectedSpecialty.template}
                          <span className="ml-2 text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white border border-trital-navy/30 text-trital-navy font-bold">{detectedSpecialty.confidence}</span>
                        </span>
                      </div>
                      <button type="button" onClick={() => { up("template", detectedSpecialty.template); toast.success(`Switched to ${(TEMPLATES.find(t => t.slug === detectedSpecialty.template) || {}).name}.`); }} className="text-[11.5px] font-semibold text-white bg-trital-navy px-3 py-1 rounded-full" data-testid="ai-use-template">
                        Use template →
                      </button>
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2 items-center">
                    <button
                      type="button"
                      onClick={runAiFormat}
                      disabled={aiBusy}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold disabled:opacity-50"
                      data-testid="ai-format-btn"
                    >
                      {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                      {aiBusy ? "Formatting…" : "Format resume"}
                    </button>
                    {aiProvider && (<span className="text-[11.5px] text-slate-500">Last formatted by: <b>{aiProvider}</b></span>)}
                  </div>
                  {aiWarning && (
                    <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-[12px] text-amber-800">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {aiWarning}
                    </div>
                  )}
                  <p className="mt-3 text-[11px] text-slate-500">
                    AI-assisted formatting must be reviewed by the candidate and a recruiter before submission. AI does not create or validate credentials.
                  </p>
                </div>
              )}

              {/* MANUAL BUILDER */}
              {mode === "manual" && (
                <div className="space-y-4" data-testid="manual-panel">
                  {/* Source-loaded confirmation — visible after upload or AI format */}
                  {(uploadedName || (aiProvider && aiText)) && (fields.fullName || aiText) && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[12.5px] text-emerald-900 flex items-start gap-2" data-testid="resume-source-banner">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
                      <div className="flex-1">
                        <b>
                          {uploadedName ? `Loaded from "${uploadedName}"` : "AI-formatted draft loaded"}
                          {aiProvider ? ` · ${aiProvider === "rule_based" ? "rule-based" : `AI (${aiProvider})`}` : ""}
                        </b>
                        <div className="text-[11.5px] text-emerald-800/90 mt-0.5">
                          Review the fields below — edit anything that looks off. When you're happy, click <b>AI Format</b> for a final polish or <b>PDF / DOCX</b> to download.
                        </div>
                      </div>
                      <button type="button" onClick={runAiFormat} disabled={aiBusy} className="text-[11px] font-semibold text-white bg-emerald-700 hover:bg-emerald-800 px-2.5 py-1 rounded-full disabled:opacity-50" data-testid="resume-source-polish">
                        AI Polish
                      </button>
                    </div>
                  )}

                  {/* Template selector */}
                  <FieldsCard title={`Active template: ${currentTemplate.name}`} testId="resume-active-template" rightSlot={
                    <button onClick={() => setMode("template")} className="text-[11.5px] font-semibold text-trital-navy underline" data-testid="resume-change-template">
                      Change template
                    </button>
                  }>
                    <div className="sm:col-span-2 text-[12px] text-slate-600">{currentTemplate.bestFor}</div>
                  </FieldsCard>

                  {/* Header */}
                  <FieldsCard title="1. Header" testId="resume-header">
                    <Field label="Full Name *"><Input value={fields.fullName} onChange={(v) => up("fullName", v)} placeholder="Jane M. Doe" testId="field-fullName" /></Field>
                    <Field label="Professional Title"><Input value={fields.title} onChange={(v) => up("title", v)} placeholder="Registered Nurse — ICU" testId="field-title" /></Field>
                    <Field label="City"><Input value={fields.city} onChange={(v) => up("city", v)} placeholder="Houston" testId="field-city" /></Field>
                    <Field label="State"><Input value={fields.state} onChange={(v) => up("state", v.toUpperCase().slice(0,2))} placeholder="TX" testId="field-state" /></Field>
                    <Field label="Phone"><Input value={fields.phone} onChange={(v) => up("phone", v)} placeholder="(555) 555-1234" testId="field-phone" /></Field>
                    <Field label="Email"><Input value={fields.email} onChange={(v) => up("email", v)} placeholder="jane.doe@example.com" testId="field-email" /></Field>
                    <Field label="LinkedIn (optional)"><Input value={fields.linkedin} onChange={(v) => up("linkedin", v)} placeholder="linkedin.com/in/janedoe" testId="field-linkedin" /></Field>
                    <Field label="License Summary (short)"><Input value={fields.licenseSummary} onChange={(v) => up("licenseSummary", v)} placeholder="RN — TX (Compact), CA" testId="field-licenseSummary" /></Field>
                  </FieldsCard>

                  {/* Summary */}
                  <FieldsCard title="2. Professional Summary" testId="resume-summary">
                    <Field label="Years of experience"><Input value={fields.yearsExp} onChange={(v) => up("yearsExp", v.replace(/\D/g,""))} placeholder="6" testId="field-yearsExp" /></Field>
                    <Field label="Specialty"><Input value={fields.specialty} onChange={(v) => up("specialty", v)} placeholder="ICU / Critical Care" testId="field-specialty" /></Field>
                    <Field label="Settings"><Input value={fields.settings} onChange={(v) => up("settings", v)} placeholder="Level-I trauma center, surgical ICU" testId="field-settings" /></Field>
                    <Field label="Key strengths"><Input value={fields.strengths} onChange={(v) => up("strengths", v)} placeholder="vasoactive drips, CRRT, charge nurse" testId="field-strengths" /></Field>
                    <Field label="Custom summary (optional — overrides auto-generated)" span={2}>
                      <textarea rows={3} value={fields.summary} onChange={(e) => up("summary", e.target.value)} placeholder="Critical-care RN with 8+ years of…" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px]" data-testid="field-summary" />
                    </Field>
                  </FieldsCard>

                  {/* Skills */}
                  <FieldsCard title="3. Core Skills" testId="resume-skills">
                    <Field label="Skills (comma or newline separated)" span={2}>
                      <textarea rows={3} value={fields.skills} onChange={(e) => up("skills", e.target.value)} placeholder="Vasoactive drips, CRRT, ACLS, charge nurse, preceptor" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px]" data-testid="field-skills" />
                      <div className="mt-2 flex flex-wrap gap-1" data-testid="skills-quick-chips">
                        <span className="text-[10.5px] text-slate-500 mr-1 self-center">Quick add:</span>
                        {currentTemplate.skills.map((s) => (
                          <button key={s} type="button" onClick={() => appendCsv("skills", s)} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 hover:bg-trital-medical-blue-soft text-slate-700" data-testid={`add-skill-${s.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                            + {s}
                          </button>
                        ))}
                      </div>
                    </Field>
                  </FieldsCard>

                  {/* Licenses & Certs */}
                  <FieldsCard title="4. Licenses & Certifications" testId="resume-licenses">
                    <Field label="Licenses (one per line — state, status, compact, expiration)" span={2}>
                      <textarea rows={3} value={fields.licenses} onChange={(e) => up("licenses", e.target.value)} placeholder={"RN — Texas (Compact), Active, expires 12/2026\nRN — California, Active, expires 03/2027"} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px] font-mono" data-testid="field-licenses" />
                    </Field>
                    <Field label="Certifications" span={2}>
                      <textarea rows={2} value={fields.certifications} onChange={(e) => up("certifications", e.target.value)} placeholder="BLS, ACLS, PALS, CCRN" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px]" data-testid="field-certifications" />
                      <div className="mt-2 flex flex-wrap gap-1">
                        {COMMON_CERTS.map((c) => (
                          <button key={c} type="button" onClick={() => appendCsv("certifications", c)} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 hover:bg-trital-medical-blue-soft text-slate-700" data-testid={`add-cert-${c.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                            + {c}
                          </button>
                        ))}
                      </div>
                    </Field>
                  </FieldsCard>

                  {/* Work Experience */}
                  <FieldsCard title="5. Work Experience" testId="resume-work">
                    <Field label="Roles — facility, role, location, dates, bullets (one role per blank-line block)" span={2}>
                      <textarea rows={8} value={fields.workExperience} onChange={(e) => up("workExperience", e.target.value)} placeholder={"Memorial Hermann TMC — Charge RN, ICU | Houston, TX | 06/2020 – Present\n- Lead 18-bed surgical ICU\n- Vasoactive drips daily"} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13px] font-mono" data-testid="field-work" />
                    </Field>
                  </FieldsCard>

                  {/* Education */}
                  <FieldsCard title="6. Education" testId="resume-education">
                    <Field label="Education" span={2}>
                      <Input value={fields.education} onChange={(v) => up("education", v)} placeholder="BSN — University of Texas at Austin, 2017" testId="field-education" />
                    </Field>
                  </FieldsCard>

                  {/* EMR */}
                  <FieldsCard title="7. EMR Systems" testId="resume-emr">
                    <div className="sm:col-span-2 flex flex-wrap gap-2">
                      {EMR_OPTIONS.map((emr) => {
                        const on = Array.isArray(fields.emr) && fields.emr.includes(emr);
                        return (
                          <button key={emr} type="button" onClick={() => toggleListItem("emr", emr)} className={`text-[12px] px-3 py-1.5 rounded-full font-semibold border ${on ? "bg-trital-navy text-white border-trital-navy" : "bg-white text-trital-navy border-slate-300"}`} data-testid={`emr-${emr.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                            {emr}
                          </button>
                        );
                      })}
                    </div>
                  </FieldsCard>

                  {/* SPECIALTY-DYNAMIC SECTION */}
                  <SpecialtyExtras template={fields.template} extras={fields.specialty_extras || {}} upExtras={upExtras} />

                  {/* Preferences */}
                  <FieldsCard title="8. Preferences" testId="resume-preferences">
                    <Field label="Assignment types" span={2}>
                      <div className="flex flex-wrap gap-2">
                        {ASSIGNMENT_TYPES.map((a) => {
                          const on = Array.isArray(fields.assignmentTypes) && fields.assignmentTypes.includes(a);
                          return (
                            <button key={a} type="button" onClick={() => toggleListItem("assignmentTypes", a)} className={`text-[12px] px-3 py-1.5 rounded-full font-semibold border ${on ? "bg-trital-navy text-white border-trital-navy" : "bg-white text-trital-navy border-slate-300"}`} data-testid={`pref-type-${a.toLowerCase()}`}>
                              {a}
                            </button>
                          );
                        })}
                      </div>
                    </Field>
                    <Field label="Shift preference">
                      <select value={fields.shiftPreference} onChange={(e) => up("shiftPreference", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px] bg-white" data-testid="field-shiftPreference">
                        <option value="">Select…</option>
                        {SHIFT_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </Field>
                    <Field label="Desired location(s)">
                      <Input value={fields.desiredLocation} onChange={(v) => up("desiredLocation", v)} placeholder="Texas, Florida, California" testId="field-desiredLocation" />
                    </Field>
                  </FieldsCard>

                  {/* Additional */}
                  <FieldsCard title="9. Additional" testId="resume-additional">
                    <Field label="Additional notes (bilingual, volunteer, awards)" span={2}>
                      <textarea rows={2} value={fields.additional} onChange={(e) => up("additional", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px]" data-testid="field-additional" />
                    </Field>
                  </FieldsCard>
                </div>
              )}

              {/* DOWNLOAD ACTIONS */}
              <div className="rounded-2xl border border-slate-200 bg-white p-4 flex flex-wrap gap-2" data-testid="resume-actions">
                <button type="button" onClick={loadSample} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold" data-testid="resume-sample-btn"><Sparkles className="w-3.5 h-3.5" /> Use Sample Data</button>
                <button type="button" onClick={reset} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-slate-300 text-[13px] text-slate-700" data-testid="resume-reset-btn"><RefreshCw className="w-3.5 h-3.5" /> Reset</button>
                <button type="button" onClick={runAiFormat} disabled={aiBusy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-indigo-600 text-white text-[13px] font-semibold disabled:opacity-50" data-testid="resume-aiformat-btn">{aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} AI Format</button>
                <button type="button" onClick={copyResume} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-300 text-[13px] text-trital-navy font-semibold" data-testid="resume-copy-btn"><Copy className="w-3.5 h-3.5" /> Copy</button>
                <button type="button" onClick={downloadTxt} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-300 text-[13px] text-trital-navy font-semibold" data-testid="resume-txt-btn"><Download className="w-3.5 h-3.5" /> TXT</button>
                <button type="button" onClick={() => downloadFile("pdf")} disabled={downloadBusy === "pdf"} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-red text-white text-[13px] font-semibold disabled:opacity-50" data-testid="resume-pdf-btn">{downloadBusy === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Download PDF{includeCoverSheet ? " + Cover" : ""}</button>
                <button type="button" onClick={() => setCoverSheetOpen(true)} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-semibold border ${includeCoverSheet ? "bg-emerald-600 text-white border-emerald-700" : "bg-white text-trital-navy border-slate-300"}`} data-testid="resume-cover-btn">
                  <Briefcase className="w-3.5 h-3.5" /> {includeCoverSheet ? "Cover sheet ON" : "Add recruiter cover"}
                </button>
                <button type="button" onClick={() => downloadFile("docx")} disabled={downloadBusy === "docx"} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold disabled:opacity-50" data-testid="resume-docx-btn">{downloadBusy === "docx" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileType2 className="w-3.5 h-3.5" />} Download DOCX</button>
                <button type="button" onClick={printResume} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-300 text-[13px] text-trital-navy font-semibold" data-testid="resume-print-btn"><Printer className="w-3.5 h-3.5" /> Print</button>
              </div>

              {/* CTA row */}
              <div className="rounded-2xl border border-slate-200 bg-trital-medical-blue-soft p-5 flex flex-wrap gap-3 items-center" data-testid="resume-final-cta">
                <span className="text-[13px] font-semibold text-trital-navy">Ready to submit?</span>
                <a href={SUBMIT_RESUME_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-red text-white text-[13px] font-semibold" data-testid="resume-cta-ceipal">
                  <FileUp className="w-3.5 h-3.5" /> Submit Resume to TRITAL Care <ExternalLink className="w-3 h-3 opacity-80" />
                </a>
                <Link to="/careers/jobs" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold" data-testid="resume-cta-jobs">
                  <Search className="w-3.5 h-3.5" /> Search Healthcare Jobs
                </Link>
                <a href={`sms:${RECRUITER_SMS_TEL}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-slate-300 text-trital-navy text-[13px] font-semibold" data-testid="resume-cta-sms">
                  <MessageSquare className="w-3.5 h-3.5" /> Text a Recruiter
                </a>
              </div>

              {/* Compliance disclaimer block */}
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="resume-compliance">
                <div className="flex items-center gap-2 overline text-amber-900">
                  <ShieldCheck className="w-3.5 h-3.5" /> COMPLIANCE & HUMAN REVIEW
                </div>
                <p className="mt-2 text-[12.5px] text-amber-900 leading-relaxed">
                  TRITAL Resume Library™ and the Resume Formatter support candidate readiness and recruiter productivity. The tool does not verify licenses,
                  certifications, employment history, eligibility, clinical competency, client approval, or hiring decisions. All resumes must be reviewed by
                  the candidate and a human recruiter before submission. AI-assisted formatting does not create or validate credentials — candidates are
                  responsible for confirming accuracy.
                </p>
              </div>
            </div>

            {/* ---------- RIGHT panel ---------- */}
            <aside className="space-y-4 lg:sticky lg:top-[100px] self-start" data-testid="resume-preview-aside">
              {/* ATS Score */}
              <div className={`rounded-2xl border p-5 ${tone.bg} ${tone.border}`} data-testid="resume-ats-card">
                <div className="flex items-center justify-between">
                  <div className="overline text-slate-600">ATS Resume Score™</div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold border ${tone.pill}`} data-testid="resume-ats-readiness">{ats.readiness}</span>
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <div className="font-display font-black text-5xl tabular-nums text-trital-navy" data-testid="resume-ats-score">{ats.score}</div>
                  <div className="text-trital-navy/60 font-semibold">/ 100</div>
                </div>
                {ats.missing.length > 0 && (
                  <ul className="mt-3 text-[12px] text-slate-700 space-y-1" data-testid="resume-ats-missing">
                    {ats.missing.slice(0, 5).map((m, i) => (<li key={i} className="flex gap-1.5"><AlertTriangle className="w-3 h-3 mt-0.5 text-amber-600 shrink-0" />{m}</li>))}
                  </ul>
                )}
              </div>

              {/* Preview */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="resume-preview-card">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h3 className="font-display font-bold text-trital-navy">Resume Preview</h3>
                  <div className="flex items-center gap-1.5 bg-slate-100 rounded-full p-0.5" data-testid="preview-mode-toggle">
                    <button
                      type="button"
                      onClick={() => setPreviewMode("ats")}
                      className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${previewMode === "ats" ? "bg-white shadow-sm text-trital-navy" : "text-slate-500"}`}
                      data-testid="preview-mode-ats"
                    >ATS View</button>
                    <button
                      type="button"
                      onClick={() => setPreviewMode("branded")}
                      className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${previewMode === "branded" ? "bg-trital-navy text-white shadow-sm" : "text-slate-500"}`}
                      data-testid="preview-mode-branded"
                    >Branded PDF View</button>
                  </div>
                </div>
                {isEmptyState && (
                  <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-1.5 text-[11.5px] text-amber-800" data-testid="preview-empty-hint">
                    Showing a sample RN resume — fill the form on the left and the preview updates live.
                  </div>
                )}
                <div className="mt-3" data-testid="resume-preview">
                  <ResumeDocumentPreview fields={previewFields} mode={previewMode} sample={isEmptyState} />
                </div>
                <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] text-slate-600" data-testid="preview-review-note">
                  <b>Review before submit:</b> Please review all details carefully. TRITAL Resume Library™ does not verify licenses, certifications, employment history, or eligibility. Candidates and recruiters must confirm all information before submission.
                </div>
              </div>

              {/* MSP readiness checklist */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="resume-msp-card">
                <div className="flex items-center gap-2 overline text-slate-500"><ShieldCheck className="w-3.5 h-3.5" /> MSP / VMS READINESS</div>
                <ul className="mt-3 space-y-1.5 text-[12.5px]">
                  {ats.checks.map((c, i) => (
                    <li key={i} className="flex items-start gap-2">
                      {c.ok ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0" /> : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-rose-500 shrink-0" />}
                      <span className={c.ok ? "text-slate-700" : "text-slate-500"}>{c.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Print CSS */}
      <style>{`
        @media print {
          .resume-print-mode header, .resume-print-mode footer,
          .resume-print-mode [data-testid="resume-methods"],
          .resume-print-mode [data-testid="resume-actions"],
          .resume-print-mode [data-testid="resume-final-cta"],
          .resume-print-mode [data-testid="upload-panel"],
          .resume-print-mode [data-testid="template-panel"],
          .resume-print-mode [data-testid="ai-panel"],
          .resume-print-mode [data-testid="score-panel"],
          .resume-print-mode [data-testid="manual-panel"],
          .resume-print-mode [data-testid="resume-ats-card"],
          .resume-print-mode [data-testid="resume-msp-card"],
          .resume-print-mode [data-testid="resume-compliance"],
          .resume-print-mode [data-testid="resume-badges"],
          .resume-print-mode [data-testid="resume-hero-stats"],
          .resume-print-mode [data-testid="wos-breadcrumb"],
          .resume-print-mode [data-testid="wos-disclaimer"] { display: none !important; }
          .resume-print-mode [data-testid="resume-preview"] { max-height: none !important; overflow: visible !important; }
        }
      `}</style>

      {/* Recruiter Cover Sheet modal */}
      {coverSheetOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="cover-sheet-modal" onClick={() => setCoverSheetOpen(false)}>
          <div className="w-full max-w-[640px] max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-trital-navy" />
                <h3 className="font-display font-bold text-trital-navy">Recruiter cover sheet (optional)</h3>
              </div>
              <button onClick={() => setCoverSheetOpen(false)} className="text-slate-400 hover:text-slate-600" data-testid="cover-sheet-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-[12.5px] text-slate-600">Add a TRITAL-branded cover page to your candidate's PDF — recruiter contact, role being submitted for, MSP client name, and a short note. Ideal for MSP/VMS submissions.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Recruiter name"><Input value={coverSheet.recruiterName} onChange={(v) => setCoverSheet((p) => ({ ...p, recruiterName: v }))} placeholder="Maria R." testId="cover-recruiterName" /></Field>
                <Field label="Recruiter email"><Input value={coverSheet.recruiterEmail} onChange={(v) => setCoverSheet((p) => ({ ...p, recruiterEmail: v }))} placeholder="maria@tritalcare.com" testId="cover-recruiterEmail" /></Field>
                <Field label="Recruiter phone"><Input value={coverSheet.recruiterPhone} onChange={(v) => setCoverSheet((p) => ({ ...p, recruiterPhone: v }))} placeholder="(832) 344-1525" testId="cover-recruiterPhone" /></Field>
                <Field label="Earliest start"><Input value={coverSheet.startDate} onChange={(v) => setCoverSheet((p) => ({ ...p, startDate: v }))} placeholder="2026-03-01 or ASAP" testId="cover-startDate" /></Field>
                <Field label="Role / Position" span={2}><Input value={coverSheet.roleSubmittedFor} onChange={(v) => setCoverSheet((p) => ({ ...p, roleSubmittedFor: v }))} placeholder="Travel RN — ICU — 13 weeks" testId="cover-role" /></Field>
                <Field label="Client / MSP"><Input value={coverSheet.client} onChange={(v) => setCoverSheet((p) => ({ ...p, client: v }))} placeholder="Memorial Hermann TMC" testId="cover-client" /></Field>
                <Field label="Pay range (optional)"><Input value={coverSheet.payRange} onChange={(v) => setCoverSheet((p) => ({ ...p, payRange: v }))} placeholder="$2,400 / wk" testId="cover-payRange" /></Field>
                <Field label="Recruiter note (1–2 lines)" span={2}>
                  <textarea rows={2} value={coverSheet.recruiterNote} onChange={(e) => setCoverSheet((p) => ({ ...p, recruiterNote: e.target.value }))} placeholder="Strong CCRN candidate, ready for trauma ICU rotations." className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13px]" data-testid="cover-note" />
                </Field>
              </div>
            </div>
            <div className="flex items-center justify-between px-5 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <button onClick={() => { setIncludeCoverSheet(false); setCoverSheetOpen(false); toast.success("Cover sheet disabled."); }} className="text-[12px] font-semibold text-slate-600" data-testid="cover-sheet-disable">Disable cover sheet</button>
              <div className="flex gap-2">
                <button onClick={() => setCoverSheetOpen(false)} className="px-4 py-2 rounded-full bg-white border border-slate-300 text-[13px] text-slate-700 font-semibold">Cancel</button>
                <button onClick={() => { setIncludeCoverSheet(true); setCoverSheetOpen(false); toast.success("Cover sheet will be included in the next PDF download."); }} className="px-4 py-2 rounded-full bg-trital-navy text-white text-[13px] font-semibold" data-testid="cover-sheet-save">Save & enable</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <RebecaMiguelChatbot variant="floating" />
    </WorkforceOsLayout>
  );
}

/* ===================================================================== */
/*  Specialty-dynamic section                                            */
/* ===================================================================== */

function SpecialtyExtras({ template, extras, upExtras }) {
  // Aliases — new RN-family templates reuse RN extras; msp_compact has no extras.
  const familyMap = { rn_staff: "rn", icu_er_medsurg: "rn" };
  const family = familyMap[template] || template;
  const title = (() => {
    switch (template) {
      case "rn": return "RN Specialty Details";
      case "rn_staff": return "RN Staff / Local Specialty Details";
      case "icu_er_medsurg": return "Critical Care / ER / Med-Surg Details";
      case "lpn": return "LPN / LVN Specialty Details";
      case "cna": return "CNA / PCT Specialty Details";
      case "allied": return "Allied Health Specialty Details";
      case "imaging": return "Imaging / Radiology Details";
      case "therapy": return "Therapy Specialty Details";
      case "np": return "Nurse Practitioner Details";
      case "physician": return "Physician Details";
      case "executive": return "Executive Healthcare Leadership Details";
      case "msp_compact": return "MSP/VMS Submission Notes";
      default: return "Specialty Details";
    }
  })();

  return (
    <FieldsCard title={title} testId={`resume-specialty-${template}`}>
      {family === "rn" && (
        <>
          <Field label="Unit specialties (Med-Surg, ICU, L&D…)" span={2}>
            <Input value={extras.unitSpecialties || ""} onChange={(v) => upExtras("unitSpecialties", v)} placeholder="Surgical ICU, trauma, post-CABG" testId="rn-unitSpecialties" />
          </Field>
          <Field label="Typical patient ratios">
            <Input value={extras.patientRatios || ""} onChange={(v) => upExtras("patientRatios", v)} placeholder="1:2 ICU" testId="rn-patientRatios" />
          </Field>
          <Field label="Floating experience">
            <Input value={extras.floating || ""} onChange={(v) => upExtras("floating", v)} placeholder="PACU, stepdown, Med-Surg" testId="rn-floating" />
          </Field>
          <Field label="Charge / Preceptor experience"><Toggle on={!!extras.chargePreceptor} onChange={(v) => upExtras("chargePreceptor", v)} testId="rn-chargePreceptor" /></Field>
          <Field label="Travel assignment experience"><Toggle on={!!extras.travelExperience} onChange={(v) => upExtras("travelExperience", v)} testId="rn-travelExperience" /></Field>
        </>
      )}
      {template === "lpn" && (
        <>
          <Field label="Setting (LTC / SNF / Rehab / Clinic)" span={2}>
            <Input value={extras.ltcSnfRehab || ""} onChange={(v) => upExtras("ltcSnfRehab", v)} placeholder="LTC + SNF" testId="lpn-setting" />
          </Field>
          <Field label="Med pass"><Toggle on={!!extras.medPass} onChange={(v) => upExtras("medPass", v)} testId="lpn-medPass" /></Field>
          <Field label="Wound care"><Toggle on={!!extras.woundCare} onChange={(v) => upExtras("woundCare", v)} testId="lpn-woundCare" /></Field>
          <Field label="Injections"><Toggle on={!!extras.injections} onChange={(v) => upExtras("injections", v)} testId="lpn-injections" /></Field>
          <Field label="Patient education"><Toggle on={!!extras.patientEducation} onChange={(v) => upExtras("patientEducation", v)} testId="lpn-patientEducation" /></Field>
        </>
      )}
      {template === "cna" && (
        <>
          <Field label="ADLs"><Toggle on={!!extras.adls} onChange={(v) => upExtras("adls", v)} testId="cna-adls" /></Field>
          <Field label="Vital signs"><Toggle on={!!extras.vitals} onChange={(v) => upExtras("vitals", v)} testId="cna-vitals" /></Field>
          <Field label="Mobility support"><Toggle on={!!extras.mobility} onChange={(v) => upExtras("mobility", v)} testId="cna-mobility" /></Field>
          <Field label="Patient transport"><Toggle on={!!extras.transport} onChange={(v) => upExtras("transport", v)} testId="cna-transport" /></Field>
          <Field label="Safety checks"><Toggle on={!!extras.safetyChecks} onChange={(v) => upExtras("safetyChecks", v)} testId="cna-safetyChecks" /></Field>
          <Field label="Specimen collection"><Toggle on={!!extras.specimens} onChange={(v) => upExtras("specimens", v)} testId="cna-specimens" /></Field>
          <Field label="12-lead EKG"><Toggle on={!!extras.ekg12} onChange={(v) => upExtras("ekg12", v)} testId="cna-ekg12" /></Field>
        </>
      )}
      {template === "allied" && (
        <>
          <Field label="Discipline">
            <Input value={extras.discipline || ""} onChange={(v) => upExtras("discipline", v)} placeholder="Respiratory Therapy / Surg Tech / SPD / Lab / Pharmacy / Dialysis / Behavioral" testId="allied-discipline" />
          </Field>
          <Field label="Procedures / Modality">
            <Input value={extras.modality || ""} onChange={(v) => upExtras("modality", v)} placeholder="Vent management, intubation assist…" testId="allied-modality" />
          </Field>
        </>
      )}
      {template === "imaging" && (
        <>
          <Field label="Primary modality">
            <Input value={extras.modality || ""} onChange={(v) => upExtras("modality", v)} placeholder="CT, MRI, X-Ray, US, Echo" testId="imaging-modality" />
          </Field>
          <Field label="ARRT credentials">
            <Input value={extras.arrt || ""} onChange={(v) => upExtras("arrt", v)} placeholder="ARRT (R)(CT)(MR)" testId="imaging-arrt" />
          </Field>
          <Field label="ARDMS credentials">
            <Input value={extras.ardms || ""} onChange={(v) => upExtras("ardms", v)} placeholder="RDMS (AB)(OB/GYN)" testId="imaging-ardms" />
          </Field>
          <Field label="Equipment">
            <Input value={extras.equipment || ""} onChange={(v) => upExtras("equipment", v)} placeholder="GE, Siemens, Philips…" testId="imaging-equipment" />
          </Field>
          <Field label="PACS"><Toggle on={!!extras.pacs} onChange={(v) => upExtras("pacs", v)} testId="imaging-pacs" /></Field>
          <Field label="Contrast"><Toggle on={!!extras.contrast} onChange={(v) => upExtras("contrast", v)} testId="imaging-contrast" /></Field>
          <Field label="Trauma imaging"><Toggle on={!!extras.trauma} onChange={(v) => upExtras("trauma", v)} testId="imaging-trauma" /></Field>
        </>
      )}
      {template === "therapy" && (
        <>
          <Field label="Discipline">
            <select value={extras.therapyDiscipline || ""} onChange={(e) => upExtras("therapyDiscipline", e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px] bg-white" data-testid="therapy-discipline">
              <option value="">Select…</option><option>PT</option><option>OT</option><option>SLP</option><option>PTA</option><option>COTA</option>
            </select>
          </Field>
          <Field label="Population">
            <Input value={extras.population || ""} onChange={(v) => upExtras("population", v)} placeholder="Adult outpatient, peds, SNF" testId="therapy-population" />
          </Field>
          <Field label="Productivity metrics">
            <Input value={extras.productivity || ""} onChange={(v) => upExtras("productivity", v)} placeholder="85% productivity, 12 patients/day" testId="therapy-productivity" />
          </Field>
          <Field label="Documentation system">
            <Input value={extras.documentation || ""} onChange={(v) => upExtras("documentation", v)} placeholder="WebPT / Optima" testId="therapy-documentation" />
          </Field>
        </>
      )}
      {template === "np" && (
        <>
          <Field label="DEA #">
            <Input value={extras.dea || ""} onChange={(v) => upExtras("dea", v)} placeholder="DEA active" testId="np-dea" />
          </Field>
          <Field label="NPI #">
            <Input value={extras.npi || ""} onChange={(v) => upExtras("npi", v)} placeholder="NPI #" testId="np-npi" />
          </Field>
          <Field label="Collaborative agreement(s)" span={2}>
            <Input value={extras.collaborative || ""} onChange={(v) => upExtras("collaborative", v)} placeholder="Physician collaboration agreement in TX, FL" testId="np-collaborative" />
          </Field>
          <Field label="Procedures">
            <Input value={extras.procedures || ""} onChange={(v) => upExtras("procedures", v)} placeholder="Suturing, joint injections, casting" testId="np-procedures" />
          </Field>
          <Field label="Avg patient volume">
            <Input value={extras.patientVolume || ""} onChange={(v) => upExtras("patientVolume", v)} placeholder="18-24/day" testId="np-volume" />
          </Field>
        </>
      )}
      {template === "physician" && (
        <>
          <Field label="Board certification" span={2}>
            <Input value={extras.boardCert || ""} onChange={(v) => upExtras("boardCert", v)} placeholder="ABIM — Internal Medicine, ABFM — Family Medicine" testId="phys-boardCert" />
          </Field>
          <Field label="Residency">
            <Input value={extras.residency || ""} onChange={(v) => upExtras("residency", v)} placeholder="UT Southwestern, 2018" testId="phys-residency" />
          </Field>
          <Field label="Fellowship">
            <Input value={extras.fellowship || ""} onChange={(v) => upExtras("fellowship", v)} placeholder="Pulmonary/Critical Care — Baylor, 2021" testId="phys-fellowship" />
          </Field>
          <Field label="Privileges" span={2}>
            <Input value={extras.privileges || ""} onChange={(v) => upExtras("privileges", v)} placeholder="Memorial Hermann, HCA Methodist" testId="phys-privileges" />
          </Field>
          <Field label="Procedures">
            <Input value={extras.procedures || ""} onChange={(v) => upExtras("procedures", v)} placeholder="Central line, paracentesis, intubation" testId="phys-procedures" />
          </Field>
          <Field label="Avg patient volume">
            <Input value={extras.patientVolume || ""} onChange={(v) => upExtras("patientVolume", v)} placeholder="14 admissions/shift" testId="phys-volume" />
          </Field>
          <Field label="State licenses" span={2}>
            <Input value={extras.stateLicenses || ""} onChange={(v) => upExtras("stateLicenses", v)} placeholder="TX, FL, CA" testId="phys-stateLicenses" />
          </Field>
        </>
      )}
      {template === "executive" && (
        <>
          <Field label="Leadership scope" span={2}>
            <Input value={extras.leadershipScope || ""} onChange={(v) => upExtras("leadershipScope", v)} placeholder="System CNO — 4 hospitals, 800-bed acute care" testId="exec-scope" />
          </Field>
          <Field label="Departments led">
            <Input value={extras.departmentsLed || ""} onChange={(v) => upExtras("departmentsLed", v)} placeholder="ICU, ED, OR, Cath Lab" testId="exec-departments" />
          </Field>
          <Field label="Staff size">
            <Input value={extras.staffSize || ""} onChange={(v) => upExtras("staffSize", v)} placeholder="450 FTE direct + matrix" testId="exec-staff" />
          </Field>
          <Field label="Budget P&L">
            <Input value={extras.budget || ""} onChange={(v) => upExtras("budget", v)} placeholder="$120M operating" testId="exec-budget" />
          </Field>
          <Field label="Quality outcomes (HCAHPS, mortality, LOS)" span={2}>
            <Input value={extras.qualityOutcomes || ""} onChange={(v) => upExtras("qualityOutcomes", v)} placeholder="HCAHPS top decile, mortality -18% YoY" testId="exec-outcomes" />
          </Field>
          <Field label="Compliance oversight" span={2}>
            <Input value={extras.compliance || ""} onChange={(v) => upExtras("compliance", v)} placeholder="Joint Commission, CMS, state DOH" testId="exec-compliance" />
          </Field>
        </>
      )}
      {template === "msp_compact" && (
        <Field label="Submission focus (1–2 lines — only what an MSP recruiter needs)" span={2}>
          <textarea rows={2} value={extras.submissionFocus || ""} onChange={(e) => upExtras("submissionFocus", e.target.value)} placeholder="13-week ICU travel · TX/FL/CA preferred · available 03/01/2026" className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px]" data-testid="msp-focus" />
        </Field>
      )}
    </FieldsCard>
  );
}

/* ===================================================================== */
/*  ResumeDocumentPreview — final polished resume preview                */
/*  Two modes:                                                           */
/*    "ats"     — plain one-column ATS-safe (no colours, single hr)      */
/*    "branded" — TRITAL-styled (navy header band + accent rules)        */
/*  Both render the SAME structured fields so PDF/DOCX/Copy/Print are    */
/*  always consistent with what the user sees.                           */
/* ===================================================================== */

function splitCsv(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((x) => String(x).trim()).filter(Boolean);
  return String(value).split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
}

function splitLines(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((x) => String(x).trim()).filter(Boolean);
  return String(value).split("\n").map((s) => s.trim()).filter(Boolean);
}

function parseWorkBlocks(work) {
  if (Array.isArray(work) && work.length && typeof work[0] === "object") return work;
  const out = [];
  if (typeof work === "string" && work.trim()) {
    for (const chunk of work.trim().split("\n\n")) {
      const lines = chunk.split("\n").map((s) => s.trim()).filter(Boolean);
      if (!lines.length) continue;
      const head = lines[0];
      const bullets = lines.slice(1).map((s) => s.replace(/^[-•]\s?/, ""));
      out.push({ head, bullets });
    }
  }
  return out;
}

function buildSpecialtyExtraLines(f) {
  const ex = f.specialty_extras || {};
  const out = [];
  const familyMap = { rn_staff: "rn", icu_er_medsurg: "rn" };
  const fam = familyMap[f.template] || f.template;
  if (fam === "rn") {
    if (ex.unitSpecialties) out.push(`Unit specialties: ${ex.unitSpecialties}`);
    if (ex.patientRatios) out.push(`Patient ratios: ${ex.patientRatios}`);
    if (ex.chargePreceptor) out.push("Charge / Preceptor experience");
    if (ex.travelExperience) out.push("Travel assignment experience");
    if (ex.floating) out.push(`Floating: ${ex.floating}`);
  } else if (f.template === "lpn") {
    if (ex.ltcSnfRehab) out.push(`Settings: ${ex.ltcSnfRehab}`);
    if (ex.medPass) out.push("Medication administration");
    if (ex.woundCare) out.push("Wound care");
    if (ex.injections) out.push("Injections");
    if (ex.patientEducation) out.push("Patient education");
  } else if (f.template === "cna") {
    if (ex.adls) out.push("ADLs");
    if (ex.vitals) out.push("Vital signs");
    if (ex.mobility) out.push("Mobility support");
    if (ex.transport) out.push("Patient transport");
    if (ex.safetyChecks) out.push("Safety checks");
    if (ex.specimens) out.push("Specimen collection");
    if (ex.ekg12) out.push("12-lead EKG");
  } else if (f.template === "allied") {
    if (ex.discipline) out.push(`Discipline: ${ex.discipline}`);
    if (ex.modality) out.push(`Modality / Procedures: ${ex.modality}`);
  } else if (f.template === "imaging") {
    if (ex.modality) out.push(`Modality: ${ex.modality}`);
    if (ex.arrt) out.push(`ARRT: ${ex.arrt}`);
    if (ex.ardms) out.push(`ARDMS: ${ex.ardms}`);
    if (ex.pacs) out.push("PACS");
    if (ex.contrast) out.push("Contrast administration");
    if (ex.trauma) out.push("Trauma imaging");
    if (ex.equipment) out.push(`Equipment: ${ex.equipment}`);
  } else if (f.template === "therapy") {
    if (ex.therapyDiscipline) out.push(`Discipline: ${ex.therapyDiscipline}`);
    if (ex.population) out.push(`Population: ${ex.population}`);
    if (ex.productivity) out.push(`Productivity: ${ex.productivity}`);
    if (ex.documentation) out.push(`Documentation: ${ex.documentation}`);
  } else if (f.template === "np") {
    if (ex.dea) out.push(`DEA: ${ex.dea}`);
    if (ex.npi) out.push(`NPI: ${ex.npi}`);
    if (ex.collaborative) out.push(`Collaborative agreements: ${ex.collaborative}`);
    if (ex.procedures) out.push(`Procedures: ${ex.procedures}`);
    if (ex.patientVolume) out.push(`Patient volume: ${ex.patientVolume}`);
  } else if (f.template === "physician") {
    if (ex.boardCert) out.push(`Board certification: ${ex.boardCert}`);
    if (ex.residency) out.push(`Residency: ${ex.residency}`);
    if (ex.fellowship) out.push(`Fellowship: ${ex.fellowship}`);
    if (ex.privileges) out.push(`Privileges: ${ex.privileges}`);
    if (ex.procedures) out.push(`Procedures: ${ex.procedures}`);
    if (ex.patientVolume) out.push(`Patient volume: ${ex.patientVolume}`);
    if (ex.stateLicenses) out.push(`State licenses: ${ex.stateLicenses}`);
  } else if (f.template === "executive") {
    if (ex.leadershipScope) out.push(`Leadership scope: ${ex.leadershipScope}`);
    if (ex.departmentsLed) out.push(`Departments led: ${ex.departmentsLed}`);
    if (ex.staffSize) out.push(`Staff size: ${ex.staffSize}`);
    if (ex.budget) out.push(`Budget P&L: ${ex.budget}`);
    if (ex.qualityOutcomes) out.push(`Quality outcomes: ${ex.qualityOutcomes}`);
    if (ex.compliance) out.push(`Compliance oversight: ${ex.compliance}`);
  } else if (f.template === "msp_compact") {
    if (ex.submissionFocus) out.push(`Submission focus: ${ex.submissionFocus}`);
  }
  return out;
}

function ResumeDocumentPreview({ fields, mode = "branded", sample = false }) {
  const f = fields || {};
  const branded = mode === "branded";

  const fullName = (f.fullName || "").trim() || "Full Name";
  const title = (f.title || "").trim();
  const loc = [f.city, f.state].filter(Boolean).join(", ");
  const contactLine = [loc, f.phone, f.email, f.linkedin].filter(Boolean).join(" · ");
  const licenseSummary = (f.licenseSummary || "").trim();
  const skills = splitCsv(f.skills);
  const certifications = splitCsv(f.certifications);
  const licenseLines = splitLines(f.licenses);
  const workBlocks = parseWorkBlocks(f.workExperience);
  const educationLines = splitLines(f.education);
  const emrList = Array.isArray(f.emr) ? f.emr : splitCsv(f.emr);
  const extraLines = buildSpecialtyExtraLines(f);
  const prefs = [];
  if (Array.isArray(f.assignmentTypes) && f.assignmentTypes.length)
    prefs.push(`Assignment types: ${f.assignmentTypes.join(", ")}`);
  if (f.shiftPreference) prefs.push(`Shift preference: ${f.shiftPreference}`);
  if (f.desiredLocation) prefs.push(`Desired location: ${f.desiredLocation}`);

  // Computed summary (mirrors backend rule)
  let summary = (f.summary || "").trim();
  if (!summary) {
    const bits = [];
    if (f.yearsExp && f.specialty)
      bits.push(`${title || "Licensed healthcare professional"} with ${f.yearsExp}+ years of experience in ${f.specialty}.`);
    if (f.settings) bits.push(`Recent settings include ${f.settings}.`);
    if (f.strengths) bits.push(`Key strengths: ${f.strengths}.`);
    summary = bits.join(" ");
  }

  const pageClass = `mx-auto bg-white shadow-sm border border-slate-200 max-h-[640px] overflow-y-auto resume-document ${branded ? "resume-document--branded" : "resume-document--ats"}`;

  return (
    <div className="bg-slate-50 rounded-lg p-3" data-testid={`resume-document-${mode}`}>
      <div
        className={pageClass}
        style={{
          width: "100%",
          maxWidth: 720,
          minHeight: 880,
          fontFamily: branded ? "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif"
                              : "Arial, Helvetica, sans-serif",
          color: "#0F172A",
          fontSize: "12.5px",
          lineHeight: 1.45,
        }}
      >
        {/* Header */}
        {branded ? (
          <div style={{ background: "linear-gradient(135deg,#13277A,#0A1A55)", color: "#fff", padding: "18px 28px" }}>
            <div style={{ fontFamily: "Inter, system-ui, sans-serif", fontWeight: 800, fontSize: 22, letterSpacing: "-0.01em" }} data-testid="doc-name">{fullName}</div>
            {title && <div style={{ fontWeight: 500, fontSize: 13, marginTop: 2, opacity: 0.9 }}>{title}</div>}
            {contactLine && <div style={{ fontSize: 11, marginTop: 6, opacity: 0.85 }}>{contactLine}</div>}
            {licenseSummary && <div style={{ fontSize: 11, marginTop: 3, opacity: 0.85 }}><b style={{ fontWeight: 700 }}>Licenses:</b> {licenseSummary}</div>}
          </div>
        ) : (
          <div style={{ padding: "22px 32px 8px 32px", borderBottom: "1px solid #94A3B8" }}>
            <div style={{ fontWeight: 800, fontSize: 22, letterSpacing: "0.02em", textTransform: "uppercase" }} data-testid="doc-name">{fullName}</div>
            {title && <div style={{ fontWeight: 600, fontSize: 13, marginTop: 2 }}>{title}</div>}
            {contactLine && <div style={{ fontSize: 11.5, marginTop: 4, color: "#334155" }}>{contactLine}</div>}
            {licenseSummary && <div style={{ fontSize: 11.5, marginTop: 2, color: "#334155" }}>Licenses: {licenseSummary}</div>}
          </div>
        )}

        <div style={{ padding: branded ? "16px 28px 24px 28px" : "12px 32px 24px 32px" }}>
          <PreviewSection title="Professional Summary" branded={branded}>
            {summary
              ? <p style={{ margin: 0 }}>{summary}</p>
              : <p style={{ margin: 0, color: "#64748B", fontStyle: "italic" }}>Add years, specialty, settings, and strengths to auto-generate a 2–4 line summary.</p>}
          </PreviewSection>

          {skills.length > 0 && (
            <PreviewSection title="Core Skills" branded={branded}>
              <ul style={{ margin: 0, paddingLeft: 16, columns: branded ? 2 : 1, columnGap: 18 }}>
                {skills.map((s, i) => (<li key={i} style={{ marginBottom: 2, breakInside: "avoid" }}>{s}</li>))}
              </ul>
            </PreviewSection>
          )}

          {(licenseLines.length > 0 || certifications.length > 0) && (
            <PreviewSection title="Licenses & Certifications" branded={branded}>
              {licenseLines.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {licenseLines.map((ln, i) => (<li key={i} style={{ marginBottom: 2 }}>{ln}</li>))}
                </ul>
              )}
              {certifications.length > 0 && (
                <p style={{ margin: "4px 0 0" }}><b style={{ fontWeight: 700, color: branded ? "#13277A" : "#0F172A" }}>Certifications:</b> {certifications.join(", ")}</p>
              )}
            </PreviewSection>
          )}

          {workBlocks.length > 0 && (
            <PreviewSection title="Professional Experience" branded={branded}>
              {workBlocks.map((job, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 700, fontSize: 12.5, color: branded ? "#13277A" : "#0F172A" }}>{job.head || [job.facility, job.role, job.location, job.dates].filter(Boolean).join(" | ")}</div>
                  {(job.bullets || []).length > 0 && (
                    <ul style={{ margin: "2px 0 0", paddingLeft: 16 }}>
                      {(job.bullets || []).slice(0, 8).map((b, j) => (<li key={j} style={{ marginBottom: 1.5 }}>{b}</li>))}
                    </ul>
                  )}
                </div>
              ))}
            </PreviewSection>
          )}

          {educationLines.length > 0 && (
            <PreviewSection title="Education" branded={branded}>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {educationLines.map((ln, i) => (<li key={i} style={{ marginBottom: 2 }}>{ln}</li>))}
              </ul>
            </PreviewSection>
          )}

          {emrList.length > 0 && (
            <PreviewSection title="EMR Systems" branded={branded}>
              <p style={{ margin: 0 }}>{emrList.join(", ")}</p>
            </PreviewSection>
          )}

          {extraLines.length > 0 && (
            <PreviewSection title="Specialty Details" branded={branded}>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {extraLines.map((ln, i) => (<li key={i} style={{ marginBottom: 2 }}>{ln}</li>))}
              </ul>
            </PreviewSection>
          )}

          {prefs.length > 0 && (
            <PreviewSection title="Preferences" branded={branded}>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {prefs.map((p, i) => (<li key={i} style={{ marginBottom: 2 }}>{p}</li>))}
              </ul>
            </PreviewSection>
          )}

          {(f.additional || "").trim() && (
            <PreviewSection title="Additional" branded={branded}>
              <p style={{ margin: 0 }}>{f.additional}</p>
            </PreviewSection>
          )}

          {branded && (
            <div style={{ marginTop: 18, paddingTop: 8, borderTop: "1px solid #E2E8F0", fontSize: 9.5, color: "#64748B" }}>
              Generated by TRITAL Resume Library™ · ATS-safe healthcare resume. Reviewed by candidate + TRITAL Care® recruiter before submission.
            </div>
          )}
        </div>
      </div>
      {sample && (
        <div className="mt-2 text-center text-[10.5px] text-slate-500">Sample preview · placeholder data</div>
      )}
    </div>
  );
}

function PreviewSection({ title, branded, children }) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={branded ? {
        fontWeight: 800, fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase",
        color: "#13277A", marginBottom: 4, paddingBottom: 3,
        borderBottom: "1.5px solid #13277A", display: "inline-block",
      } : {
        fontWeight: 800, fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase",
        color: "#0F172A", marginBottom: 4, borderBottom: "1px solid #334155",
        paddingBottom: 2, display: "block",
      }}>
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

/* ===================================================================== */
/*  Small atoms                                                          */
/* ===================================================================== */

function Field({ label, children, span = 1 }) {
  return (
    <div className={span === 2 ? "sm:col-span-2" : ""}>
      <label className="block text-[12.5px] font-semibold text-slate-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, testId }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-trital-navy/30"
      data-testid={testId}
    />
  );
}

function Toggle({ on, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold border ${on ? "bg-emerald-600 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-300"}`}
      data-testid={testId}
    >
      {on ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
      {on ? "Yes" : "Add"}
    </button>
  );
}

function FieldsCard({ title, children, testId, rightSlot }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display font-bold text-[15px] text-trital-navy">{title}</h3>
        {rightSlot}
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">{children}</div>
    </section>
  );
}
